import { z } from "zod";
import { HeroFormatSchema } from "@scrile/domain/season";

/**
 * Контракт API v3 между apps/platform и фронтами apps/hero, apps/studio: часть героя.
 * Студия и панель — в studio.ts; index.ts только реэкспортирует оба файла, чтобы
 * между ними не было цикла (studio.ts берёт общие схемы отсюда).
 *
 * Правила:
 * - все пути под /api/v1; сервер определяет героя по Host (локально — заголовок x-hero);
 * - ошибки всегда { error: код, message: текст для человека };
 * - деньги в центах, время — ISO-строки в UTC, номера серий — с 1.
 *
 * Здесь — то, что нужно сайту героя в волне 1. Студия и переписка добавляют свои схемы
 * рядом, не меняя существующие поля.
 */

export const ApiErrorSchema = z.object({
  error: z.enum([
    "validation",
    "not_found",
    "unauthorized",
    "forbidden",
    "payment_required",
    "conflict",
    "rate_limited",
    "internal",
  ]),
  message: z.string(),
  /** Только у validation: какие поля не прошли и почему. */
  issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/** Тема героя: то, из чего шаблон делает CSS-переменные. */
export const HeroThemeSchema = z.object({
  accent: z.string(),
  background: z.string(),
  surface: z.string(),
  text: z.string(),
  /** Семейство из Google Fonts или системное. */
  font: z.string(),
  logoUrl: z.string().nullable(),
  portraitUrl: z.string().nullable(),
  coverUrl: z.string().nullable(),
});
export type HeroTheme = z.infer<typeof HeroThemeSchema>;

export const HeroPricingSchema = z.object({
  currency: z.literal("usd"),
  monthlyCents: z.number().int().nonnegative(),
  yearlyCents: z.number().int().nonnegative(),
  /** null — «всё сразу» у этого героя выключено. */
  allAtOnceCents: z.number().int().nonnegative().nullable(),
  trialDays: z.number().int().nonnegative(),
  /** Стендовый тестовый платёж: разовый суточный доступ, без реального списания. */
  demoDayPass: z
    .object({
      priceCents: z.number().int().nonnegative(),
      durationHours: z.number().int().positive(),
    })
    .nullable()
    .optional(),
});
export type HeroPricing = z.infer<typeof HeroPricingSchema>;

/** GET /api/v1/hero — публичный герой по хосту. */
export const HeroPublicSchema = z.object({
  slug: z.string(),
  name: z.string(),
  format: HeroFormatSchema,
  tagline: z.string(),
  description: z.string(),
  trailerUrl: z.string().nullable(),
  theme: HeroThemeSchema,
  pricing: HeroPricingSchema,
  freeEpisodes: z.number().int().nonnegative(),
  creator: z.object({ name: z.string(), slug: z.string(), url: z.string() }),
  /** Разрешил ли креатор короткие ответы герою в аудио. */
  repliesEnabled: z.boolean(),
  /** Текущий опубликованный сезон; null — ещё ничего не опубликовано. */
  currentSeason: z
    .object({
      ordinal: z.number().int().positive(),
      title: z.string(),
      episodes: z.number().int().nonnegative(),
    })
    .nullable(),
  legal: z.object({ termsUrl: z.string(), privacyUrl: z.string() }),
});
export type HeroPublic = z.infer<typeof HeroPublicSchema>;

/**
 * Карточка общей витрины Scrile AI. Каталог намеренно короче полной страницы героя:
 * здесь нет цен, persona и служебного статуса — только то, что нужно выбрать историю.
 */
export const HeroCatalogItemSchema = z.object({
  slug: z.string(),
  name: z.string(),
  format: HeroFormatSchema,
  tagline: z.string(),
  description: z.string(),
  url: z.string().url(),
  portraitUrl: z.string().nullable(),
  coverUrl: z.string().nullable(),
  accent: z.string(),
  background: z.string(),
});
export type HeroCatalogItem = z.infer<typeof HeroCatalogItemSchema>;

/** GET /api/v1/heroes — все публичные герои для галереи общего лендинга. */
export const HeroCatalogSchema = z.object({ heroes: z.array(HeroCatalogItemSchema) });
export type HeroCatalog = z.infer<typeof HeroCatalogSchema>;

export const SubscriptionStatusSchema = z.enum([
  "trialing",
  "active",
  "past_due",
  "canceled",
  "paused",
]);

/**
 * GET /api/v1/me — кто вошёл и что ему доступно у этого героя.
 * Тот же ответ возвращают register (201) и login (200): фронт после входа сразу
 * знает и подписку, и имя.
 *
 * Пробный период — это статус `trialing` у подписки с выбранным планом, а не
 * отдельный план: после пробных дней списание идёт по этому плану.
 */
export const MeSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  timezone: z.string(),
  subscription: z
    .object({
      status: SubscriptionStatusSchema,
      plan: z.enum(["monthly", "yearly"]),
      currentPeriodEnd: z.string(),
      cancelAtPeriodEnd: z.boolean(),
    })
    .nullable(),
  /** Куплено «всё сразу» для текущего сезона. */
  unlockedAll: z.boolean(),
  /** Конец стендового суточного доступа; отсутствует/NULL вне demo-режима. */
  demoAccessUntil: z.string().nullable().optional(),
  /** Пробный период ещё не использован у этого героя: можно обещать «7 days free». */
  trialEligible: z.boolean(),
  /**
   * Роль на платформе: admin открывает панель платформы в студии. Необязательное, чтобы
   * старые клиенты и моки без поля оставались валидными; сервер отдаёт его всегда.
   */
  role: z.enum(["user", "admin"]).optional(),
});
export type Me = z.infer<typeof MeSchema>;

/**
 * Имя подписчика озвучивается голосом героя, поэтому оно строже обычного поля:
 * только буквы, пробел, дефис и апостроф (прямой или типографский — его подставляет
 * iOS), до трёх слов — иначе через него можно заставить голос креатора произнести
 * что угодно.
 */
export const DisplayNameSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[\p{L}\p{M}]+(?:[ '’-][\p{L}\p{M}]+){0,2}$/u);

export const RegisterRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
  displayName: DisplayNameSchema,
  ageConfirmed: z.literal(true),
  timezone: z.string().min(1),
  /** Код креатора или партнёра из ссылки, если был. */
  code: z.string().max(32).optional(),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/** GET /api/v1/episodes — серии сезона в порядке выхода (audio). Ответ — голый массив. */
export const EpisodeSummarySchema = z.object({
  ordinal: z.number().int().positive(),
  title: z.string(),
  free: z.boolean(),
  lengthSec: z.number().int().nonnegative(),
  /** Когда серия вышла для этого подписчика; null — ещё не вышла. */
  releasedAt: z.string().nullable(),
  /** Когда выйдет, если ещё не вышла; null — неизвестно (нет подписки). */
  scheduledAt: z.string().nullable(),
  /** Можно слушать: серия вышла и доступ есть. */
  available: z.boolean(),
  /** Не хватает подписки: показать пейвол. */
  locked: z.boolean(),
  listenedSec: z.number().int().nonnegative(),
});
export type EpisodeSummary = z.infer<typeof EpisodeSummarySchema>;
export const EpisodesResponseSchema = z.array(EpisodeSummarySchema);

/**
 * POST /api/v1/episodes/:ordinal/start — подписчик начал слушать серию. Первый вызов
 * закрепляет за ним текущую ревизию сезона; ответ — 204.
 */

/**
 * GET /api/v1/episodes/:ordinal — что нужно плееру. Бесплатная серия отдаётся и гостю
 * без входа — общей дорожкой без имени; остальным нужен доступ, иначе 402.
 */
export const EpisodeManifestSchema = z.object({
  ordinal: z.number().int().positive(),
  title: z.string(),
  /** Дорожка серии с уже вставленным именем подписчика. */
  audioUrl: z.string(),
  lengthSec: z.number().int().nonnegative(),
  transcript: z.array(
    z.object({
      startSec: z.number().nonnegative(),
      endSec: z.number().nonnegative(),
      text: z.string(),
    }),
  ),
  /** Карточки героев, упомянутых в серии, с моментом появления. */
  heroCards: z.array(
    z.object({
      atSec: z.number().nonnegative(),
      slug: z.string(),
      name: z.string(),
      tagline: z.string(),
      portraitUrl: z.string().nullable(),
      url: z.string(),
    }),
  ),
});
export type EpisodeManifest = z.infer<typeof EpisodeManifestSchema>;

/** GET /api/v1/extras — рубрики и голосовые, вышедшие для подписчика (audio). */
export const ExtraSchema = z.object({
  id: z.string(),
  kind: z.enum(["rubric", "voice_note"]),
  title: z.string(),
  releasedAt: z.string(),
  /** Пустая строка, если заперто: адрес отдаётся только с доступом. */
  audioUrl: z.string(),
  lengthSec: z.number().int().nonnegative(),
  /** Нужна подписка: показать пейвол. */
  locked: z.boolean(),
});
export type Extra = z.infer<typeof ExtraSchema>;
export const ExtrasResponseSchema = z.array(ExtraSchema);

/**
 * POST /api/v1/touch — касание ссылки креатора или партнёра (`?ref=CODE` на странице
 * героя). Сервер запоминает его за пользователем или гостевой кукой; при оформлении
 * подписки по нему решается, кто привёл. Ответ — 204.
 */
export const TouchRequestSchema = z.object({
  /** Код креатора или партнёра (до 32 знаков) или карточка героя `hero:<slug>` (слаг до 32 плюс префикс). */
  code: z.string().min(4).max(64),
});

/** POST /api/v1/checkout — начать оплату; ответ — адрес Stripe Checkout. */
export const CheckoutRequestSchema = z.object({
  plan: z.enum(["monthly", "yearly", "all_at_once"]),
  code: z.string().max(32).optional(),
});
export const CheckoutResponseSchema = z.object({ url: z.string() });

/** POST /api/v1/portal — кабинет подписки в Stripe. */
export const PortalResponseSchema = z.object({ url: z.string() });

/** POST /api/v1/tips — подарок герою; оплата через Stripe Checkout, ответ — адрес. */
export const TipRequestSchema = z.object({
  amountCents: z.number().int().min(100).max(50_000),
  message: z.string().max(200).optional(),
});
export const TipResponseSchema = z.object({ url: z.string() });

/**
 * Куда Stripe возвращает человека на сайт героя. Сервер подставляет их в
 * Checkout и Customer Portal; фронт держит эти адреса как маршруты.
 */
export const RETURN_PATHS = {
  checkoutSuccess: (plan: "monthly" | "yearly" | "all_at_once") =>
    `/listen?checkout=success&plan=${plan}`,
  checkoutCanceled: "/plans?checkout=canceled",
  portalReturn: "/account",
  tipSuccess: "/listen?gift=thanks",
  /** Подарок отменён в Checkout — обратно в плеер, без пометки: отказ от подарка не ошибка. */
  tipCanceled: "/listen",
} as const;

/** POST /api/v1/events — аналитика с фронта: только виды, без текста. Ответ — 204 без тела. */
export const ClientEventSchema = z.object({
  kind: z.enum([
    "page_view",
    "pilot_started",
    "pilot_finished",
    "paywall_shown",
    "checkout_started",
    "episode_started",
    "episode_finished",
  ]),
  episode: z.number().int().positive().optional(),
  screen: z.enum(["hero", "auth", "plans", "listen", "account", "chat"]).optional(),
});

/**
 * Лента для шаблона «Мессенджер» (GET /api/v1/feed?after=<seq>&wait=1): элементы outbox
 * подписчика у героя по seq, только уже видимые, до 100 за раз; пустой ответ с wait=1
 * держится до 25 с. Ответ — голый массив (FeedResponseSchema).
 *
 * Тело — по виду элемента (discriminated union по kind). Тела открытые (passthrough):
 * сервер может дописать служебные поля, клиент читает только объявленные.
 * - text — реплика героя или сообщение подписчика (from: user, с inboxId);
 * - voice — голосовое: text — расшифровка (null, пока не распознано), url — личная
 *   подписанная ссылка на звук, если он есть;
 * - photo, video — файл сцены: asset — имя в медиатеке, url — подписанная ссылка
 *   (нет — файл удалён из медиатеки после публикации);
 * - hero_card — карточка другого героя со ссылкой на его сайт (`?ref=hero:<slug>` того,
 *   кто её прислал);
 * - episode_ready / extra_ready — вышла серия или выпуск рубрики/голосового (аудио);
 * - system — служебное (type: reminder — напоминание, пока его не пишет dialogue).
 * Реплики сцены несут номер серии, сцены и реплики (episode, scene, line, key).
 */
export const FEED_ITEM_KINDS = [
  "text",
  "voice",
  "photo",
  "video",
  "hero_card",
  "episode_ready",
  "extra_ready",
  "system",
] as const;
export const FeedItemKindSchema = z.enum(FEED_ITEM_KINDS);

const SceneLineRef = {
  episode: z.number().int().positive().optional(),
  scene: z.number().int().positive().optional(),
  line: z.number().int().nonnegative().optional(),
  key: z.string().optional(),
};

export const FeedTextBodySchema = z
  .object({ ...SceneLineRef, text: z.string(), inboxId: z.string().optional() })
  .passthrough();
export const FeedVoiceBodySchema = z
  .object({ ...SceneLineRef, text: z.string().nullable(), url: z.string().optional(), inboxId: z.string().optional() })
  .passthrough();
export const FeedMediaBodySchema = z.object({ ...SceneLineRef, asset: z.string(), url: z.string().optional() }).passthrough();
export const FeedHeroCardBodySchema = z
  .object({
    ...SceneLineRef,
    slug: z.string(),
    name: z.string(),
    url: z.string(),
    tagline: z.string(),
    portraitUrl: z.string().nullable(),
  })
  .passthrough();
export const FeedEpisodeReadyBodySchema = z
  .object({ episode: z.number().int().positive(), title: z.string(), revisionId: z.string() })
  .passthrough();
export const FeedExtraReadyBodySchema = z
  .object({
    id: z.string(),
    kind: z.enum(["rubric", "voice_note"]),
    key: z.string(),
    title: z.string(),
    releasedAt: z.string(),
    revisionId: z.string(),
  })
  .passthrough();
export const FeedSystemBodySchema = z
  .object({
    type: z.string(),
    text: z.string().optional(),
    episode: z.number().int().positive().optional(),
    scene: z.number().int().positive().optional(),
  })
  .passthrough();

const feedItem = <K extends (typeof FEED_ITEM_KINDS)[number], B extends z.ZodTypeAny>(kind: K, body: B) =>
  z.object({
    seq: z.number().int().nonnegative(),
    kind: z.literal(kind),
    from: z.enum(["hero", "user"]),
    body,
    at: z.string(),
  });

export const FeedItemSchema = z.discriminatedUnion("kind", [
  feedItem("text", FeedTextBodySchema),
  feedItem("voice", FeedVoiceBodySchema),
  feedItem("photo", FeedMediaBodySchema),
  feedItem("video", FeedMediaBodySchema),
  feedItem("hero_card", FeedHeroCardBodySchema),
  feedItem("episode_ready", FeedEpisodeReadyBodySchema),
  feedItem("extra_ready", FeedExtraReadyBodySchema),
  feedItem("system", FeedSystemBodySchema),
]);
export type FeedItem = z.infer<typeof FeedItemSchema>;
export type FeedItemKind = FeedItem["kind"];
/** GET /api/v1/feed — голый массив, как у серий. */
export const FeedResponseSchema = z.array(FeedItemSchema);

/**
 * POST /api/v1/messages — сообщение герою: ровно одно из полей — текст или ключ уже
 * загруженного голосового (`inbox/<userId>/…`). Ответ 202 — MessageAcceptedSchema.
 * Частота ограничена по подписчику (429 с Retry-After).
 */
export const MessageRequestSchema = z.union([
  z.object({ text: z.string().trim().min(1).max(2000) }).strict(),
  z.object({ voiceAssetKey: z.string().min(1).max(300) }).strict(),
]);
export type MessageRequest = z.infer<typeof MessageRequestSchema>;

/** Ответ 202 на сообщение: его номер в ленте. */
export const MessageAcceptedSchema = z.object({ seq: z.number().int().nonnegative() });
export type MessageAccepted = z.infer<typeof MessageAcceptedSchema>;

/**
 * Подсказка ленты для «печатает…» (волна 3, мессенджер). Лента отдаёт только видимое, а
 * пузыри сцены записаны заранее с паузами: чтобы показать «печатает» перед следующим, как в
 * мессенджере, клиенту нужно знать, когда он станет виден. Сервер кладёт в ответ
 * GET /feed (и пустой, и непустой) два заголовка про первый ещё не видимый элемент после
 * последнего отданного:
 * - x-feed-next-in — через сколько миллисекунд он станет виден (целое ≥ 0). Интервал, а не
 *   момент: часы телефона и сервера расходятся, а интервал от этого не зависит;
 * - x-feed-next-kind — его вид (FEED_ITEM_KINDS): голосовое — «записывает», фото — «отправляет».
 * Нет заголовков — ничего не ждёт (или сервер старый): клиент просто не показывает «печатает».
 */
export const FEED_NEXT_IN_HEADER = "x-feed-next-in";
export const FEED_NEXT_KIND_HEADER = "x-feed-next-kind";

/**
 * Известные мессенджеру типы тела system (FeedSystemBodySchema.type остаётся строкой — новые
 * типы не ломают старых клиентов, незнакомый с text показывается заметкой, без text — никак):
 * - reminder — напоминание при молчании, рисуется репликой героя (text — её текст);
 * - busy — герой «занят до следующей серии» (лимит расхода, пауза между сериями); text
 *   необязателен, без него клиент пишет своё;
 * - paywall — сезон дошёл до платной серии без доступа (episode — номер ждущей серии):
 *   мессенджер показывает пейвол сразу, не дожидаясь 402 на отправке.
 */
export const FEED_SYSTEM_TYPES = ["reminder", "busy", "paywall"] as const;

/**
 * POST /api/v1/messages — необязательный ключ повтора: клиент шлёт один и тот же ключ при
 * повторе того же сообщения после сбоя сети. Сервер может по нему не принимать дубль
 * (ответить тем же 202 { seq }); не смотрит — клиент всё равно сверяет зеркало в ленте.
 */
export const MESSAGE_IDEMPOTENCY_HEADER = "idempotency-key";

/**
 * POST /api/v1/messages/voice — загрузка голосового подписчика до POST /messages.
 * multipart/form-data: поле `file` — звук (один файл, до VOICE_UPLOAD_MAX_BYTES, тип из
 * VOICE_UPLOAD_TYPES, параметры вроде `;codecs=opus` допустимы), поле `durationMs` —
 * длина записи по часам браузера (строка с целым, до VOICE_MAX_DURATION_MS; сервер может
 * перепроверить по файлу). Ответ 201 — VoiceUploadResponseSchema: ключ `inbox/{userId}/…`,
 * который затем уходит в POST /messages { voiceAssetKey }. Ошибки: 401 без входа,
 * 402 без доступа (как у сообщений), 413/validation — больше лимита или не звук,
 * 429 — частота как у сообщений.
 */
export const VOICE_UPLOAD_MAX_BYTES = 2 * 1024 * 1024;
export const VOICE_MAX_DURATION_MS = 60_000;
/**
 * Что пишет MediaRecorder: Chrome и Firefox — webm/ogg с opus, Safari (iOS 14.3+) — mp4 с
 * AAC; wav и mpeg — на случай загрузки готового файла.
 */
export const VOICE_UPLOAD_TYPES = [
  "audio/webm",
  "audio/ogg",
  "audio/mp4",
  "audio/x-m4a",
  "audio/aac",
  "audio/mpeg",
  "audio/wav",
] as const;
export const VoiceUploadResponseSchema = z.object({ voiceAssetKey: z.string().min(1).max(300) });
export type VoiceUploadResponse = z.infer<typeof VoiceUploadResponseSchema>;

// Уведомления и приватность (волна 3) -------------------------------------------------

/**
 * GET /api/v1/push/public-key — публичный ключ VAPID (applicationServerKey для
 * pushManager.subscribe, base64url). null — пуш на сервере не настроен: не предлагать.
 */
export const PushPublicKeySchema = z.object({ publicKey: z.string().nullable() });
export type PushPublicKey = z.infer<typeof PushPublicKeySchema>;

const Base64UrlKey = z.string().regex(/^[A-Za-z0-9_-]+={0,2}$/, "Expected a base64url key");

/**
 * POST /api/v1/push/subscribe — тело: PushSubscription.toJSON() браузера как есть. Только
 * вошедший и только на сайте героя: подписка браузера живёт на origin сайта (у каждого
 * поддомена свой service worker), и сервер шлёт на неё уведомления только этого героя.
 * endpoint — только известных служб пуша (Google, Mozilla, Apple, Microsoft), иначе 400.
 * Ответ 204; повтор того же endpoint обновляет ключи (и владельца, если на устройстве
 * вошёл другой человек).
 */
export const PushSubscribeRequestSchema = z.object({
  endpoint: z.string().url().max(2048),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    /** Открытый ключ устройства P-256: 65 байт, в base64url — 87 знаков. */
    p256dh: Base64UrlKey.min(40).max(200),
    /** Секрет аутентификации: 16 байт, в base64url — 22 знака. */
    auth: Base64UrlKey.min(8).max(100),
  }),
});
export type PushSubscribeRequest = z.infer<typeof PushSubscribeRequestSchema>;

/** DELETE /api/v1/push/subscribe — отписать это устройство; 204 и тогда, когда подписки нет. */
export const PushUnsubscribeRequestSchema = z.object({ endpoint: z.string().url().max(2048) });

/**
 * Что получает service worker в событии push (event.data.json()): показать
 * showNotification(title, { body, tag, icon, data: { url } }), по клику — открыть url (он
 * на origin этого же сайта героя). tag — одинаковый у уведомлений одного вида и героя:
 * новое заменяет прежнее, а не копится стопкой. Текста реплик в уведомлении нет намеренно:
 * экран блокировки видят посторонние.
 */
export const PushPayloadSchema = z.object({
  kind: z.enum(["episode", "message"]),
  title: z.string(),
  body: z.string(),
  url: z.string(),
  tag: z.string(),
  icon: z.string().nullable(),
});
export type PushPayload = z.infer<typeof PushPayloadSchema>;

/**
 * GET /api/v1/me/notifications — настройки уведомлений подписчика (одни на платформу, не
 * на героя). Служебные письма (пробный период, оплата, подтверждение подписки) приходят
 * всегда — от них не отписываются, их требует сама подписка.
 * - emailNewEpisodes — письма «вышла новая серия» (не чаще одного в сутки на героя и только
 *   если человек не заходил на сайт после выхода);
 * - push — пуш «новая серия» и «герой написал» на подписанные устройства;
 * - pushAvailable — пуш настроен на сервере (есть ключ VAPID);
 * - pushDevices — сколько устройств подписано на сайте этого героя.
 */
export const NotificationSettingsSchema = z.object({
  emailNewEpisodes: z.boolean(),
  push: z.boolean(),
  pushAvailable: z.boolean(),
  pushDevices: z.number().int().nonnegative(),
});
export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;

/** PATCH /api/v1/me/notifications — любое непустое подмножество; ответ — NotificationSettingsSchema. */
export const NotificationSettingsPatchSchema = z
  .object({ emailNewEpisodes: z.boolean(), push: z.boolean() })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: "Nothing to change" });
export type NotificationSettingsPatch = z.infer<typeof NotificationSettingsPatchSchema>;

/**
 * DELETE /api/v1/me — удалить аккаунт, подтвердив паролем. Ответ 204, кука сессии снята.
 * Неверный пароль — 400 (issues: password); креатор с героями — 409 «передайте или закройте
 * героев»; получатель выплат партнёрской программы — 409. Подписки отменяются в Stripe сразу,
 * без возврата за остаток периода. После удаления на ту же почту можно зарегистрироваться
 * заново — это будет новый аккаунт без истории.
 */
export const MeDeleteRequestSchema = z.object({ password: z.string().min(1).max(200) });
export type MeDeleteRequest = z.infer<typeof MeDeleteRequestSchema>;

const ExportHeroSchema = z.object({ slug: z.string(), name: z.string() });

/**
 * GET /api/v1/me/export — всё, что платформа хранит о человеке, одним JSON (файлом:
 * Content-Disposition: attachment). Не чаще раза в сутки: чаще — 429 с Retry-After. Время —
 * ISO в UTC, деньги — центы. Реквизитов карт здесь нет — их нет и у платформы (они у
 * Stripe). Голосовые — подписанными ссылками на 24 часа, открываются только с сессией
 * владельца. extra — разделы других модулей по имени (например, разговоры с героем).
 */
export const MeExportSchema = z.object({
  exportedAt: z.string(),
  account: z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string(),
    timezone: z.string(),
    language: z.string(),
    role: z.enum(["user", "admin"]),
    createdAt: z.string(),
    displayNameChangedAt: z.string().nullable(),
  }),
  /** Согласия: возраст 18+ и условия (версия — дата редакции). */
  consents: z.object({
    ageConfirmedAt: z.string(),
    termsVersion: z.string(),
    termsAcceptedAt: z.string(),
  }),
  notifications: z.object({
    emailNewEpisodes: z.boolean(),
    push: z.boolean(),
    devices: z.array(
      z.object({
        hero: ExportHeroSchema.nullable(),
        userAgent: z.string().nullable(),
        createdAt: z.string(),
        lastUsedAt: z.string().nullable(),
      }),
    ),
  }),
  subscriptions: z.array(
    z.object({
      hero: ExportHeroSchema,
      plan: z.enum(["monthly", "yearly"]),
      status: SubscriptionStatusSchema,
      startedAt: z.string(),
      currentPeriodEnd: z.string(),
      cancelAtPeriodEnd: z.boolean(),
      canceledAt: z.string().nullable(),
    }),
  ),
  /** Счета: подписки и разовые покупки. */
  payments: z.array(
    z.object({
      hero: ExportHeroSchema,
      for: z.enum(["subscription", "all_at_once", "demo_day", "tip"]),
      status: z.string(),
      amountCents: z.number().int(),
      taxCents: z.number().int(),
      refundedCents: z.number().int(),
      currency: z.string(),
      periodStart: z.string().nullable(),
      periodEnd: z.string().nullable(),
      paidAt: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
  purchases: z.array(
    z.object({
      hero: ExportHeroSchema,
      kind: z.enum(["all_at_once", "demo_day", "tip"]),
      status: z.string(),
      amountCents: z.number().int(),
      currency: z.string(),
      /** Записка к подарку герою. */
      message: z.string().nullable(),
      paidAt: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
  /** Кто привёл к герою: ссылка или код креатора, партнёра, платформы. */
  referrals: z.array(
    z.object({
      hero: ExportHeroSchema,
      source: z.enum(["creator", "partner", "platform"]),
      method: z.enum(["link", "code"]).nullable(),
      code: z.string().nullable(),
      touchedAt: z.string(),
    }),
  ),
  progress: z.array(
    z.object({
      hero: ExportHeroSchema,
      episode: z.number().int(),
      scene: z.number().int(),
      state: z.string(),
      unlockedAll: z.boolean(),
      startedAt: z.string(),
      updatedAt: z.string(),
    }),
  ),
  /** Лента: реплики героя, выпуски серий, свои сообщения — как их видит подписчик. */
  feed: z.array(
    z.object({
      hero: ExportHeroSchema,
      seq: z.number().int(),
      kind: z.string(),
      from: z.enum(["hero", "user"]),
      body: z.record(z.unknown()),
      at: z.string(),
    }),
  ),
  /** Свои сообщения герою: текст или голосовое (расшифровка — в text). */
  messages: z.array(
    z.object({
      hero: ExportHeroSchema,
      kind: z.enum(["text", "voice"]),
      text: z.string().nullable(),
      voiceUrl: z.string().nullable(),
      durationMs: z.number().int().nullable(),
      sentAt: z.string(),
    }),
  ),
  /** Что герой запомнил о человеке. */
  memory: z.array(
    z.object({ hero: ExportHeroSchema, key: z.string(), value: z.string(), source: z.string(), updatedAt: z.string() }),
  ),
  /** Имя, озвученное голосом героя (сам звук не выгружается: это голос креатора). */
  nameClips: z.array(z.object({ hero: ExportHeroSchema, displayName: z.string(), renderedAt: z.string() })),
  /** Письма: только вид и статус — текстов писем платформа не хранит. */
  emails: z.array(
    z.object({ template: z.string(), status: z.string(), hero: ExportHeroSchema.nullable(), createdAt: z.string(), sentAt: z.string().nullable() }),
  ),
  /** События сайта (аналитика): только виды и номера серий. */
  activity: z.array(
    z.object({ kind: z.string(), hero: ExportHeroSchema.nullable(), episode: z.number().int().nullable(), at: z.string() }),
  ),
  /** Вызовы AI-поставщиков по подписчику: число по месяцам, без сумм и без текста. */
  aiUsage: z.array(
    z.object({
      month: z.string(),
      hero: ExportHeroSchema.nullable(),
      operation: z.string(),
      provider: z.string(),
      calls: z.number().int().nonnegative(),
    }),
  ),
  creator: z
    .object({
      name: z.string(),
      slug: z.string(),
      status: z.string(),
      tier: z.string(),
      country: z.string().nullable(),
      createdAt: z.string(),
      heroes: z.array(ExportHeroSchema),
    })
    .nullable(),
  partner: z.object({ code: z.string(), status: z.string(), createdAt: z.string() }).nullable(),
  privacyRequests: z.array(z.object({ kind: z.enum(["export", "delete"]), at: z.string() })),
  extra: z.record(z.unknown()),
});
export type MeExport = z.infer<typeof MeExportSchema>;

export const ROUTES = {
  hero: "/api/v1/hero",
  catalogHeroes: "/api/v1/heroes",
  me: "/api/v1/me",
  register: "/api/v1/auth/register",
  login: "/api/v1/auth/login",
  logout: "/api/v1/auth/logout",
  episodes: "/api/v1/episodes",
  episode: (ordinal: number) => `/api/v1/episodes/${ordinal}`,
  episodeStart: (ordinal: number) => `/api/v1/episodes/${ordinal}/start`,
  extras: "/api/v1/extras",
  touch: "/api/v1/touch",
  checkout: "/api/v1/checkout",
  stripeWebhook: "/api/v1/stripe/webhook",
  portal: "/api/v1/portal",
  tips: "/api/v1/tips",
  events: "/api/v1/events",
  /** Манифест PWA героя по Host: имя и цвета — из темы героя. */
  manifest: "/manifest.webmanifest",
  feed: "/api/v1/feed",
  messages: "/api/v1/messages",
  /** Загрузка голосового подписчика (multipart) → { voiceAssetKey } для POST /messages. */
  messageVoice: "/api/v1/messages/voice",
  /** GET — публичный ключ VAPID для pushManager.subscribe (PushPublicKeySchema). */
  pushPublicKey: "/api/v1/push/public-key",
  /** POST — подписать устройство (PushSubscribeRequestSchema), DELETE — отписать ({ endpoint }); оба 204. */
  pushSubscribe: "/api/v1/push/subscribe",
  /** GET и PATCH — настройки уведомлений (NotificationSettingsSchema, NotificationSettingsPatchSchema). */
  meNotifications: "/api/v1/me/notifications",
  /** GET — выгрузка своих данных (MeExportSchema), не чаще раза в сутки; чаще — 429 с Retry-After. */
  meExport: "/api/v1/me/export",
  /** DELETE — удалить аккаунт (MeDeleteRequestSchema) → 204. Тот же путь, что у me, другой метод. */
  meDelete: "/api/v1/me",
  /**
   * Отписка от писем о новых сериях по ссылке из письма (?t=…): GET — страница с кнопкой,
   * POST — отписать (так же шлёт почтовый клиент по List-Unsubscribe-Post, RFC 8058).
   * Ответ — HTML-страница, а не JSON: её открывают из письма, а не из приложения.
   */
  emailUnsubscribe: "/api/v1/notifications/unsubscribe",
} as const;
